import { SupabaseClient } from "@supabase/supabase-js";

import { Database } from "../../supabase.types";
import { slugifyWithoutHyphens } from "@punchup/utils";
import type { ComedianRow } from "../../legacy/comedian";
import type {
  ComedianCandidate,
  ComedianNameResolution,
} from "../../models/comedians";

export type { ComedianCandidate, ComedianNameResolution };

export class ComedianNameTakenError extends Error {
  constructor(name: string, slug: string) {
    super(`Comedian name or slug already taken: ${name} (${slug})`);
    this.name = "ComedianNameTakenError";
  }
}

export class ComedianCreateError extends Error {
  constructor(name: string, cause: string) {
    super(`Failed to create comedian ${name}: ${cause}`);
    this.name = "ComedianCreateError";
  }
}

/** Letters, digits, spaces and hyphens only, single-spaced. */
export function sanitizeComedianName(name: string): string {
  return name
    .trim()
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .replace(/\s+/g, " ");
}

async function isComedianNameOrSlugTaken(
  supabase: SupabaseClient<Database>,
  displayName: string,
  slug: string
): Promise<boolean> {
  const [byName, bySlug] = await Promise.all([
    supabase
      .from("comedians")
      .select("id")
      .eq("display_name", displayName)
      .limit(1)
      .throwOnError(),
    supabase
      .from("comedians")
      .select("id")
      .eq("slug", slug)
      .limit(1)
      .throwOnError(),
  ]);
  return (byName.data?.length ?? 0) > 0 || (bySlug.data?.length ?? 0) > 0;
}

/**
 * Inserts a comedian nobody has claimed yet (`is_punchup_verified: false`),
 * refusing when the sanitized name or its slug already exists. The display
 * name is stored as given; only the uniqueness check and the slug use the
 * sanitized form.
 */
export async function createUnverifiedComedian(
  supabase: SupabaseClient<Database>,
  name: string,
  extras: Pick<Partial<ComedianRow>, "about" | "about_img"> = {}
): Promise<ComedianRow> {
  const sanitizedName = sanitizeComedianName(name);
  const slug = slugifyWithoutHyphens(sanitizedName);

  if (await isComedianNameOrSlugTaken(supabase, sanitizedName, slug)) {
    throw new ComedianNameTakenError(sanitizedName, slug);
  }

  const { data, error } = await supabase
    .from("comedians")
    .insert([
      {
        display_name: name,
        display_name_short: name,
        slug,
        is_punchup_verified: false,
        ...(extras.about_img && { about_img: extras.about_img }),
        ...(extras.about && { about: extras.about }),
      },
    ])
    .select("*")
    .single();

  if (error || !data) {
    throw new ComedianCreateError(sanitizedName, error?.message ?? "no row");
  }
  return data;
}

function escapeLikePattern(value: string): string {
  return value.replace(/[\\%_]/g, (char) => `\\${char}`);
}

/**
 * Double-quotes a value for a PostgREST filter value. PostgREST reads a quoted value with
 * backslash escapes, so `\\` and `"` are escaped; commas and parentheses are then safe inside
 * an `or=(...)` list.
 */
function quoteFilterValue(value: string): string {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

// Names per `or=(...)` request, to keep the URL well under proxy limits.
const NAME_LOOKUP_CHUNK = 100;

function dedupeById(rows: ComedianCandidate[]): ComedianCandidate[] {
  const byId = new Map<string, ComedianCandidate>();
  for (const row of rows) byId.set(row.id, row);
  return [...byId.values()];
}

/**
 * Matches display names (a crawler lineup, a CSV column) to comedians by
 * case-insensitive `display_name` equality or by slug equality. One candidate
 * is `matched`, several are `ambiguous`, none is `unmatched`. Read-only.
 */
export async function resolveComedianNames(
  supabase: SupabaseClient<Database>,
  names: string[]
): Promise<ComedianNameResolution[]> {
  const uniqueNames = [...new Set(names.map((name) => name.trim()))].filter(
    Boolean
  );
  if (uniqueNames.length === 0) return [];

  const slugs = [...new Set(uniqueNames.map(slugifyWithoutHyphens))].filter(
    Boolean
  );
  // `or=(display_name.ilike."A",...)`, not `ilikeAnyOf`: prod runs PostgREST v10, which has no
  // `ilike(any)` quantifier (v11+) and rejects it as "Unexpected param or filter missing
  // operator" (PGRST104). Without `*` or `%`, ilike is a case-insensitive equality.
  const nameFilters = uniqueNames.map(
    (name) => `display_name.ilike.${quoteFilterValue(escapeLikePattern(name))}`
  );
  const nameChunks: string[][] = [];
  for (let i = 0; i < nameFilters.length; i += NAME_LOOKUP_CHUNK) {
    nameChunks.push(nameFilters.slice(i, i + NAME_LOOKUP_CHUNK));
  }

  const [bySlug, ...byName] = await Promise.all([
    supabase
      .from("comedians")
      .select("id, display_name, slug")
      .in("slug", slugs)
      .throwOnError(),
    ...nameChunks.map((chunk) =>
      supabase
        .from("comedians")
        .select("id, display_name, slug")
        .or(chunk.join(","))
        .throwOnError()
    ),
  ]);

  const candidates = dedupeById([
    ...(bySlug.data ?? []),
    ...byName.flatMap((result) => result.data ?? []),
  ]);

  return uniqueNames.map((name) => {
    const lowerName = name.toLowerCase();
    const slug = slugifyWithoutHyphens(name);
    const matches = candidates.filter(
      (candidate) =>
        candidate.display_name.toLowerCase() === lowerName ||
        (slug !== "" && candidate.slug === slug)
    );
    return {
      name,
      status:
        matches.length === 1
          ? "matched"
          : matches.length > 1
            ? "ambiguous"
            : "unmatched",
      candidates: matches,
    };
  });
}
